import type { ReshapeTarget } from "@/hooks/useBoxReshape";
import { pointsToGeom } from "@/utils/box-drawing-utils";
import { type CornerIndex, cornerRing } from "@/utils/oriented-box";
import { cn } from "@/utils/utils";
import type { PointerEvent } from "react";

interface Props {
  /** The box as drawn right now (live while dragging), normalized. */
  points: number[][];
  /** `useBoxReshape`'s `grab`. */
  grab: (target: ReshapeTarget) => {
    onPointerDown: (e: PointerEvent<HTMLElement>) => void;
    onPointerMove: (e: PointerEvent<HTMLElement>) => void;
    onPointerUp: (e: PointerEvent<HTMLElement>) => void;
  };
  /** The handles' border color. */
  className?: string;
}

/**
 * Resize handles on a box's actual corners — a tilted box's, not its
 * bounding box's — positioned in the box div, which spans that bounding box.
 */
export const BoxCornerHandles = ({ points, grab, className }: Props) => {
  const { minX, minY, maxX, maxY } = pointsToGeom(points);
  const width = maxX - minX;
  const height = maxY - minY;
  if (!(width > 0 && height > 0)) return null;

  return (
    <>
      {cornerRing(points).map(([x, y], i) => {
        const left = ((x - minX) / width) * 100;
        const top = ((y - minY) / height) * 100;
        // Top-left / bottom-right of the box's center resize along "\".
        const cursor =
          (left - 50) * (top - 50) >= 0
            ? "cursor-nwse-resize"
            : "cursor-nesw-resize";
        return (
          <div
            key={i}
            data-corner={i}
            {...grab(i as CornerIndex)}
            className={cn(
              "absolute z-[60] size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white border-2",
              cursor,
              className,
            )}
            style={{ left: `${left}%`, top: `${top}%` }}
          />
        );
      })}
    </>
  );
};
